import { Box, Typography } from "@mui/material"
import { StatusDone, StatusCharging, StatusFailed } from "components/status"
import { SessionCellComponent, SessionInfo, SessionsTableColumnsType } from "types/sessions"
import { StyledTableCell } from "components/tables/StyledTable"
import { useSessionsContext } from "layouts/sessions/context/sessions-context"
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded"
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded"
import { useCallback, useEffect, useState } from "react"
import { SortOrder, SORTORDER } from "types/sort"
import { SessionsStatus } from "layouts/sessions/utils/const"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import Tooltip from "@mui/material/Tooltip"
import { AlertType } from "layouts/charging-rules/utils/utils.ts"
import { formatPrice } from "layouts/sessions/utils/functions.ts"
import Flash from "assets/svg/autocharge-flash.svg?react"
import RfidCard from "assets/svg/rfid-card.svg?react"
import { capitalize } from "lib/utils"
import { useTheme } from "@mui/material/styles"

type SessionCellProps = {
    session: SessionInfo
}

export const StatusSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    const getStatusComponent = () => {
        switch (session.status) {
            case SessionsStatus.Finished:
                return <StatusDone text="Terminée" />
            case SessionsStatus.Active:
            case SessionsStatus.Pending:
                return <StatusCharging text="En charge" />
            case SessionsStatus.Unknown:
            case SessionsStatus.Expired:
            case SessionsStatus.Failed:
                return <StatusFailed text="Échouée" />
            default:
                return <StatusFailed text="Échouée" />
        }
    }

    return getStatusComponent()
}

export const DateSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    const startingDate =
        "startingDate" in session ? session.startingDate : session.starting_date
    const stoppedDate =
        "stoppedDate" in session ? session.stoppedDate : session.stopped_date
    const sessionDate = new Date(startingDate)
    const [currentDuration, setCurrentDuration] = useState<string>("")
    const date = sessionDate.toLocaleDateString("fr-FR")
    const dateTime = sessionDate.toLocaleTimeString("fr-FR", {
        hour12: false,
        hour: "numeric",
        minute: "numeric",
    })
    const endTime = stoppedDate
        ? new Date(stoppedDate).toLocaleTimeString("fr-FR", {
            hour12: false,
            hour: "numeric",
            minute: "numeric",
          })
        : null

    useEffect(() => {
        const calculateDuration = () => {
            const now = new Date()
            const stoppedDate =
                "stoppedDate" in session
                    ? session.stoppedDate
                    : session.stopped_date
            const delta = stoppedDate
                ? Math.abs(
                      new Date(stoppedDate).getTime() - sessionDate.getTime()
                  )
                : Math.abs(now.getTime() - sessionDate.getTime()) // si une charge est en cours on affiche la duree en cours

            const deltaDays = Math.floor(delta / 86400_000)
            const deltaHours = Math.floor((delta % 86400_000) / 3600_000)
            const deltaMinutes = Math.floor((delta % 3600_000) / 60_000)

            let deltaTime = ""
            if (deltaDays > 0) deltaTime += deltaDays + "j "
            if (deltaHours > 0 || deltaDays > 0) deltaTime += deltaHours + "h "
            deltaTime += (deltaMinutes < 10 ? "0" : "") + deltaMinutes + " min"

            setCurrentDuration(deltaTime)
        }
        calculateDuration()
        const interval = setInterval(() => {
            if (
                "stoppedDate" in session
                    ? session.stoppedDate
                    : session.stopped_date
            ) {
                calculateDuration()
            }
        }, 1000)

        return () => clearInterval(interval)
    }, [stoppedDate, sessionDate])

    return (
        <>
            {session.status === SessionsStatus.Unknown ||
            session.status == SessionsStatus.Failed ||
            session.status == SessionsStatus.Expired ? (
                <Box sx={{ whiteSpace: "preserve-breaks" }}>
                    Le : <b>{date}</b>
                    <br />
                    Durée : <b>0 min</b>
                </Box>
            ) : (
                <Box sx={{ whiteSpace: "preserve-breaks" }}>
                    Le : <b>{date}</b>
                    <br />
                    De : <b>{dateTime}</b> à <b>{endTime}</b>
                    <br />
                    Durée : <b>{currentDuration}</b>
                </Box>
            )}
        </>
    )
}

export const RfidSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    const theme = useTheme()
    const method =
        session.autocharge === true
            ? "autocharge"
            : session.autocharge === false && session.rfidUuid != null
              ? "rfid"
              : session.rfidNumber
    const label =
        method === "autocharge"
            ? capitalize(method)
            : method === "rfid"
              ? method.toUpperCase()
              : session.rfidNumber
    const vAlign = method === "rfid" ? "none" : "middle"
    return (
        <Box sx={{ textAlign: "center", color: theme.palette.primary.main }}>
            <span style={{ marginRight: "3px", verticalAlign: "middle" }}>
                {method === "autocharge" ? (
                    <Flash />
                ) : method === "rfid" ? (
                    <RfidCard />
                ) : (
                    ""
                )}
            </span>
            <span style={{ verticalAlign: vAlign, marginLeft: "5px" }}>
                {label}
            </span>
            {label == "RFID" && <span><br />{session.rfidNumber}</span>}
        </Box>
    )
}

export const CardHolderSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    const cardHolder =
        "cardHolder" in session ? session.cardHolder : session.card_holder
    return <>{cardHolder}</>
}

export const AddressSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    const stationAddress =
        "stationAddress" in session
            ? session.stationAddress
            : session.station_address
    const stationName =
        "stationName" in session ? session.stationName : session.station_name
    return (
        <Box sx={{ whiteSpace: "preserve-breaks" }}>
            <Typography variant="titleSmall">{`Station ${stationName}`}</Typography>
            <Typography>{stationAddress}</Typography>
        </Box>
    )
}

export const KwhSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    return <>{session.kwh.toFixed(3)} kWh</>
}

export const PriceSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    return session.price === null ? (
        <></>
    ) : (
        <>{formatPrice(session.price, "fr-FR")} </>
    )
}

export const AlertTriggerSessionCell: SessionCellComponent = ({
    session,
}: SessionCellProps) => {
    const getAlertMessages = () => {
        const alerts =
            "triggeredAlerts" in session
                ? session.triggeredAlerts
                : session.triggered_alerts
        if (!alerts || alerts.length === 0) return null
        return alerts
            .map((alert: AlertType) => {
                switch (alert) {
                    case AlertType.DOWAlert:
                        return "Alerte jour de la semaine"
                    case AlertType.HoursAlert:
                        return "Alerte horaire"
                    case AlertType.MaxDuration:
                        return "Alerte durée"
                    case AlertType.MaxKWH:
                        return "Alerte énergie"
                    default:
                        return alert
                }
            })
            .join(", ")
    }
    const alertMessage = getAlertMessages()

    if (!alertMessage || alertMessage.length === 0) return null

    return (
        <Tooltip
            title={alertMessage}
            sx={{
                tooltip: {
                    backgroundColor: "background.paper",
                    color: "text.primary",
                    boxShadow: 1,
                    fontSize: "0.875rem",
                    border: "1px solid",
                    borderColor: "divider",
                },
            }}
        >
            <WarningAmberRoundedIcon sx={{ color: "primary.main" }} />
        </Tooltip>
    )
}

export const SessionHeaderCell = ({
    keySession,
    title,
    width,
}: {
    keySession: keyof SessionsTableColumnsType
    title: string
    width: string
}) => {
    const { orderBy, sortOrder, setOrderBy, setSortOrder } =
        useSessionsContext()

    const onSort = useCallback(
        (orderBy: keyof SessionsTableColumnsType) => {
            setOrderBy(orderBy)
            setSortOrder((sortOrder: SortOrder) =>
                sortOrder === SORTORDER.ASC ? SORTORDER.DESC : SORTORDER.ASC
            )
        },
        [setOrderBy, setSortOrder]
    )
    return (
        <StyledTableCell
            onClick={() => onSort(keySession)}
            sx={{
                width: width,
                cursor: "pointer",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    width:
                        orderBy !== keySession ? "calc(100% + 0.5rem)" : "100%",
                }}
            >
                {title}
                {orderBy === keySession && sortOrder === SORTORDER.ASC && (
                    <ArrowDropUpRoundedIcon
                        sx={{
                            p: 0,
                            m: 0,
                            ml: "16px",
                            height: "0.5rem",
                            width: "0.5rem",
                            transform: "scale(3.5)",
                            color: "grey.600",
                        }}
                        data-testid="arrow-up-sort-table-sessions"
                    />
                )}
                {orderBy === keySession && sortOrder === SORTORDER.DESC && (
                    <ArrowDropDownRoundedIcon
                        sx={{
                            p: 0,
                            m: 0,
                            ml: "16px",
                            height: "0.5rem",
                            width: "0.5rem",
                            transform: "scale(3.5)",
                            color: "grey.600",
                        }}
                        data-testid="arrow-down-sort-table-sessions"
                    />
                )}
            </Box>
        </StyledTableCell>
    )
}
